import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ChevronRightIcon,
} from "@common/components/icons";
import classNames from "classnames";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { Fragment } from "react";
import { useRouter } from "next/router";
import { PurchaseStep } from "../types/PurchaseStep";

type Props = {
  steps: PurchaseStep[];
  currentStep: PurchaseStep;
  previousStep: PurchaseStep | undefined;
};

const PurchaseStepper = ({ steps, currentStep, previousStep }: Props) => {
  const { t } = useTranslation(["common", "purchase"]);
  const {
    query: { source },
  } = useRouter();

  const backButtonLabel = previousStep?.nameTranslation
    ? t("purchase:back_to_previous_step", {
        previousStep: t(previousStep.nameTranslation),
      })
    : t("common:back");

  return (
    <>
      <div className="lg:hidden w-full flex items-center justify-between mt-5 mb-1 order-3 xl:order-2 ">
        <h2 className="body-3 leading-6">{t(currentStep.nameTranslation)}</h2>
        {previousStep && (
          <Link
            className="flex gap-x-2 items-center body-3 text-primary-bold"
            href={{
              pathname: previousStep.href,
              query: { source },
            }}
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span>{backButtonLabel}</span>
          </Link>
        )}
      </div>

      <div className="flex w-full items-center order-3 xl:order-2 justify-center lg:justify-between xl:justify-start caption-1 text-base text-text-light-200">
        {steps.map((step) => (
          <Fragment key={step.order}>
            {step.order > 1 && <ChevronRightIcon className="w-8 h-8" />}
            <div
              className={classNames("flex", {
                "text-primary-bold": step.order === currentStep.order,
                "text-text-light-200": step.order > currentStep.order,
                "text-text-light-400": step.order < currentStep.order,
              })}
            >
              {step.order < currentStep.order && (
                <CheckCircleIcon className="mr-1.5 w-6 h-6" />
              )}
              {step.order >= currentStep.order && (
                <span
                  className={classNames(
                    "flex items-center justify-center mr-2 m-0.5 rounded-full text-center font-medium text-white w-5 h-5",
                    {
                      "bg-primary-bold": step.order === currentStep.order,
                      "bg-text-light-200": step.order > currentStep.order,
                      "bg-text-light-300": step.order < currentStep.order,
                    }
                  )}
                >
                  <span className="mt-0.5">{step.order}</span>
                </span>
              )}
              <span className="hidden lg:block">{t(step.nameTranslation)}</span>
            </div>
          </Fragment>
        ))}
      </div>
    </>
  );
};

export default PurchaseStepper;
